
import AddressDropdown from "./AddressDropdown.vue";
import _ from "lodash";
import Vue, { PropType } from "vue";
import { mapActions, mapGetters, mapMutations } from "vuex";
import avsCheck, { IChangedFlags } from "~/assets/utils/Avs/avsCheck";
import checkIfDetailUpdated from "~/assets/utils/checkIfDetailUpdated";
import updateDetails from "~/assets/utils/updateDetails";
import resolveStateCountryObjectsUtil from "~/assets/utils/resolveStateCountryObjects";
import { isRegionEntry } from "~/assets/utils/getAvsParams";
import {
  getCarrierRestriction,
  getCarrierWarning,
} from "~/assets/utils/carrierAddressGuards";
import { AVSSubStatus } from "~/entities/AVS";
import { Details } from "~/entities/Details";
import Place from "~/entities/Place";
import { Country } from "~/entities/Response/FormData";
import Trip from "~/entities/Trip";
import NoticeEntity from "~/entities/Notice";

const AVS_MESSAGE_KEYS = {
  verified: "Verified",
  unitRequired: "UnitRequired",
  errorAddress: "ErrorAddress",
  errorOutOfRange: "ErrorOutOfRange",
  errorNotActive: "NotActiveForDelivery",
  errorIncomplete: "AddressIncomplete",
  apiFailed: "ApiFailed",
  // BIK-2135: pending copy from Bill/Travis; falls back to the string below
  // until the DB row exists.
  errorCarrierRestricted: "CarrierRestricted",
  zoneOnly: "ZoneOnly",
} as const;

// BIK-2148: the UPS Store copy already exists as its own alert rows — the Search-time
// notice used them. They are NOT prefixed with "AVSMessage-", so they are fetched
// separately from the loop above rather than added to AVS_MESSAGE_KEYS.
const UPS_STORE_ALERT_NAMES: Record<string, string> = {
  upsStoreFrom: "AlertShipFromUPS",
  upsStoreTo: "AlertShipToUPS",
};

const AVS_MESSAGE_FALLBACKS: Record<string, string> = {
  verified: "Address verified.",
  unitRequired: "Apartment or Suite number required to validate.",
  errorAddress: "Please enter a valid address.",
  errorOutOfRange: "Street number out of range.",
  errorNotActive: "Address is valid but not currently active for delivery.",
  errorIncomplete: "Please complete the address below.",
  apiFailed:
    "We couldn't validate this address. You may continue but delivery issues may occur.",
  errorCarrierRestricted:
    "We can't ship to FedEx or USPS locations or PO Boxes. Please enter a different address.",
  // BIK-2148: a Google pick that resolves to a city/ZIP with no street. Nothing is wrong
  // with it — we deliberately do not validate it, since partial addresses are allowed so
  // the customer can get a quote. Says what happened AND what will be needed later, so the
  // full-address requirement at step 3 doesn't arrive as a surprise.
  zoneOnly:
    "Quote based on city and ZIP. A full street address is required to complete your shipment.",
  // BIK-2148: only used if the DB rows are missing — the real copy comes from
  // AlertShipFromUPS / AlertShipToUPS, which already existed.
  upsStoreFrom:
    "This looks like a UPS Store. Please affix the label yourself and ask for a drop-off receipt.",
  upsStoreTo:
    "This looks like a UPS Store. Please call ahead to confirm they will accept your shipment.",
};

export default Vue.extend({
  components: { AddressDropdown },
  props: {
    validated: { type: Boolean, default: false },
    googleObj: {
      type: Object as PropType<google.maps.places.AutocompleteOptions>,
      default: null,
    },
    allowedCountries: {
      type: Array as PropType<Country[]>,
      default: () => [],
    },
    states: {
      type: Array as PropType<any[]>,
      default: () => [],
    },
    preSelectionTrip: {
      type: Object as PropType<Trip>,
      default: () => new Trip(),
    },
    index: {
      type: Number,
      default: 0,
    },
    options: {
      type: Array as PropType<any[]>,
      default: () => [],
    },
    outShip: {
      type: Boolean,
      default: false,
    },
    userAddresses: {
      type: Array as PropType<any[]>,
      default: () => [],
    },
    validationErrors: {
      type: Array as () => Array<unknown>,
      default: () => [],
    },
  },
  data() {
    return {
      trip: new Trip(),
      sameLocationWarning: false, // BIK-2084: non-blocking same from/to location warning
      errorFrom: false, // for future new errors
      errorTo: false, // for future new errors
      toInternational: false,
      fromInternational: false,
      toCountry: "",
      fromCountry: "",
      // BIK-1995 pre-rework warning state, used when avsFeedbackEnabled is off.
      warningFrom: false,
      warningTo: false,
      isLoadingFrom: false as boolean,
      isLoadingTo: false as boolean,
      avsCheckSeq: { from: 0, to: 0 } as { from: number; to: number },
      avsMessages: {} as Record<string, NoticeEntity | null>,
    };
  },
  computed: {
    ...mapGetters({
      details: "ship/details",
      hasAdvancedInStep: "ship/hasAdvancedInStep",
      trips: "ship/trips",
      ratesAreValid: "ship/ratesAreValid",
    }),
    // BIK-2095: paste-direct-to-AVS flag — the master switch for the ship step 1 AVS
    // rework. OFF in prod restores the pre-rework behavior (yellow warning + global
    // alert, no inline messages/highlights).
    avsRawPasteDirect(): boolean {
      return (this as any).$config?.avsRawPasteDirect === true;
    },
    // Gate for the inline AVS feedback (message + field state). Kept as a named computed
    // because it reads better than the raw flag at the call sites below.
    // BIK-2127: was `avsRawPasteDirect || avsShip1Enabled`; collapsed when the legacy
    // AVS_SHIP1_REWORK flag was removed.
    avsFeedbackEnabled(): boolean {
      return (this as any).avsRawPasteDirect;
    },
    // BIK-2107: GetRates failed for this trip (trip-level, not split by from/to).
    // Passed to both AddressDropdowns to reveal the segmented fallback form.
    getRatesFailed(): boolean {
      return (
        (this as any).avsRawPasteDirect &&
        !!(this as any).ratesAreValid &&
        (this as any).ratesAreValid.valid === false
      );
    },
  },
  watch: {
    details: {
      deep: true,
      immediate: true,
      handler(newVal) {
        if (newVal[this.index]) {
          const from = newVal[this.index]?.from;
          const to = newVal[this.index]?.to;
          if (!(this as any).avsFeedbackEnabled) {
            // Pre-rework behavior (rework flag OFF): yellow field warning
            // + global "warningAVSFailed" alert when AVS fails on a full
            // (non-partial) address.
            const fromFailed =
              from?.avsChecked &&
              !from?.passedAvs &&
              from?.searchType?.toLowerCase() !== "partial";
            const toFailed =
              to?.avsChecked &&
              !to?.passedAvs &&
              to?.searchType?.toLowerCase() !== "partial";
            this.warningFrom = !!fromFailed;
            this.warningTo = !!toFailed;
            if (fromFailed || toFailed) {
              this.requestAlert({ name: "warningAVSFailed", type: "address" });
            }
            return;
          }
          const fromFailedApi =
            (from?.avsStatus?.subStatus === AVSSubStatus.apiAVSFailed ||
              from?.avsStatus?.subStatus === AVSSubStatus.validLicenseKey) &&
            from?.searchType?.toLowerCase() !== "partial";
          const toFailedApi =
            (to?.avsStatus?.subStatus === AVSSubStatus.apiAVSFailed ||
              to?.avsStatus?.subStatus === AVSSubStatus.validLicenseKey) &&
            to?.searchType?.toLowerCase() !== "partial";
          if (fromFailedApi || toFailedApi) {
            // BIK-1995: top banner removed — AVS failure now shown inline via avsMessage("apiFailed")
            // this.requestAlert({ name: "warningAVSFailed", type: "address" });
          }
        }
      },
    },
  },
  async mounted() {
    if ((this as any).preSelectionTrip) {
      (this as any).trip = new Trip((this as any).preSelectionTrip);
    } else {
      (this as any).trip = new Trip();
    }
    // BIK-1995/BIK-2095: the inline DB messages back both the rework feedback and the
    // paste-direct feedback; preload them when either flag is on.
    if ((this as any).avsFeedbackEnabled) {
      await (this as any).preloadAvsMessages();
    }
  },
  methods: {
    // Vuex actions
    ...mapMutations({
      pushAlert: "errorsBag/addAlert",
      clearAlert: "errorsBag/clearAlert",
      setTrip: "ship/setTrip",
      setDetail: "ship/setDetail",
      clearDetail: "ship/clearDetail",
      setHasAdvancedInStep: "ship/setHasAdvancedInStep",
    }),
    ...mapActions({
      requestAlert: "errorsBag/requestAlert",
      removeNotice: "errorsBag/removeNotice",
      getAlert: "errorsBag/getAlert",
    }),
    async preloadAvsMessages(): Promise<void> {
      const states = Object.keys(AVS_MESSAGE_KEYS) as Array<keyof typeof AVS_MESSAGE_KEYS>;
      const results = await Promise.all(
        states.map((s) => (this as any).getAlert({ name: `AVSMessage-${AVS_MESSAGE_KEYS[s]}` }))
      );
      states.forEach((s, i) => {
        (this as any).$set(this.avsMessages, s, results[i]);
      });
      // BIK-2148: the UPS Store rows are fetched by their own names — no "AVSMessage-"
      // prefix — because they already existed for the Search-time alert.
      const upsKeys = Object.keys(UPS_STORE_ALERT_NAMES);
      const upsResults = await Promise.all(
        upsKeys.map((k) =>
          (this as any).getAlert({ name: UPS_STORE_ALERT_NAMES[k] })
        )
      );
      upsKeys.forEach((k, i) => {
        (this as any).$set(this.avsMessages, k, upsResults[i]);
      });
    },
    // get country info by filtering the address_components property
    getTripCountry(place: google.maps.places.PlaceResult) {
      // if null, no country information returned from places
      return place.address_components?.find(function (el) {
        return el.types.includes("country");
      });
    },
    getCountryFromPlace(place: google.maps.places.PlaceResult) {
      let country;
      // If address and address_components then filter address_components by types to get country
      if (place && place.address_components) {
        country = place.address_components.filter(function (el) {
          return el.types.includes("country");
        });
        // if null, no country information from places
        return country.length ? country[0].long_name : null;
      }
    },
    // check if the selected place is valid, direction as parameter to avoid duplicated code
    isValidAddress(
      Address: google.maps.places.PlaceResult | any,
      direction: String
    ) {
      (this as any).trip[direction as string] = Address;
      const country = this.getTripCountry(Address);
      // BIK-1995: If allowedCountries hasn't loaded yet, skip the country
      // validation entirely. Otherwise a fast user gets a false "We do not
      // currently offer shipping services from <country>" while the list is
      // still in flight.
      if (!this.allowedCountries || this.allowedCountries.length === 0) {
        if (direction === "to") {
          this.errorTo = false;
          this.toCountry = country?.short_name ?? "";
        } else {
          this.errorFrom = false;
          this.fromCountry = country?.short_name ?? "";
        }
        return Address;
      }
      const isCountryAllowed = this.allowedCountries.filter(
        (c: Country) => c.code === country?.short_name && c.active
      );
      if (
        !isCountryAllowed.length &&
        typeof country !== "undefined" &&
        country?.long_name
      ) {
        this.$emit("emitError", { direction, country: country?.long_name }); // <- leave this to step or remove
        if (direction === "to") {
          this.errorTo = true;
          this.toCountry = country.short_name;
        } else {
          this.errorFrom = true;
          this.fromCountry = country.short_name;
        }
        return;
      } else {
        if (direction === "to") {
          this.errorTo = false;
          this.toCountry =
            country && country.short_name ? country.short_name : "";
        } else {
          this.errorFrom = false;
          this.fromCountry =
            country && country.short_name ? country.short_name : "";
        }
        if (this.toCountry === "AU" && this.toCountry === this.fromCountry) {
          this.errorTo = true;
          this.errorFrom = true;
          this.$emit("emitError", {
            direction,
            country: "within " + country?.long_name,
          });
        } else this.$emit("errorClean", direction); // <- leave this to step
      }
      return Address;
    },
    assignLocation(address: Place, direction: string) {
      // if the Address is valid then assign to trips.
      this.setTrip({
        direction,
        value: this.isValidAddress(address, direction),
        index: this.index ? this.index : 0,
      });
      const country = this.getTripCountry(address);
      this.$emit("countryChanged", {
        shortName: country?.short_name,
        index: this.index ? this.index : 0,
      });
      this.checkAddressForBusiness(address, direction);
      this.checkIfInternational(address, direction);
      this.checkIfEqual();
    },
    assignUserDetails(
      {
        stageDetails,
        updatedDetails,
        changedFlags,
      }: {
        stageDetails: Details;
        updatedDetails: Details;
        changedFlags: IChangedFlags;
      },
      direction: string
    ) {
      const storePostalCode =
        this.details?.[this.index]?.[direction].postalCode;

      const inputPostalCode = stageDetails.postalCode;
      if (storePostalCode?.includes(inputPostalCode)) {
        stageDetails.postalCode = storePostalCode;
      }

      // BIK-1995: warning alerts moved to API-failure subStatuses only (see details watcher).
      // Other AVS errors now surface as inline UI via avsUiState instead of global alerts.
      if (stageDetails) {
        stageDetails.avsChecked = true;
        this.setDetail({
          direction,
          value: "clear",
          index: this.index ? this.index : 0,
        });
        this.setDetail({
          direction,
          value: stageDetails,
          index: this.index ? this.index : 0,
        });
        this.$emit("emitDetailsUpdated", {
          updatedDetails,
          direction,
          changedFlags,
        });
      } else {
        this.clearDetail({ direction, index: this.index ? this.index : 0 });
      }
      this.checkIfEqual();
    },
    checkIfEqual() {
      // BIK-2084: same from/to location is no longer a red full-stop error.
      // It surfaces as a non-blocking orange warning + the "AlertSameLocation"
      // notice (copy comes from the DB alert).
      // BIK-2127: this used to be skipped under the legacy AVS_SHIP1_REWORK flag, which
      // was never enabled anywhere, so it always ran. Guard dropped with the flag —
      // behavior is unchanged. Surfacing same-location as an inline avsUiState message
      // (instead of the orange border + top notice) is still an open follow-up.
      // BIK-2148: read the trip from the STORE, not from the local `trip` data field.
      //
      // `trip` is seeded once in mounted() from the preSelectionTrip prop and then only
      // ever written by isValidAddress() — clearInputAddress() resets the store copy and
      // leaves this one untouched. So after clearing Ship To and picking a saved address
      // for Ship From, the store had `to` empty while the local copy still held the old
      // Place: isSameLocation() compared from against a stale to, matched, and raised
      // "Did you mean to ship from and to the same place?" for an empty Ship To.
      //
      // Wrapped in `new Trip()` because the getter returns a plain reactive object and
      // isSameLocation is a method on the class. Falls back to the local copy so nothing
      // changes on a stage whose store trip does not exist yet.
      const storeTrip = (this as any).trips?.[this.index ? this.index : 0];
      const tripToCheck = storeTrip ? new Trip(storeTrip) : this.trip;
      const same = tripToCheck ? tripToCheck.isSameLocation() : false;
      // Kept as a dedicated flag (not warningFrom/warningTo) so the AVS watcher
      // above doesn't overwrite it on every details change.
      this.sameLocationWarning = same;
      if (same) {
        this.requestAlert({ name: "AlertSameLocation", type: "address" });
      } else {
        this.removeNotice("AlertSameLocation");
      }
    },
    checkAddressForBusiness(address: Place, direction: string) {
      let formattedAddress = address.originalFormattedAddress
        ? address.originalFormattedAddress
        : (address.formatted_address as string);
      if (formattedAddress) {
        formattedAddress = formattedAddress.toLowerCase();
      } else {
        formattedAddress = "";
      }
      const addressComponent = address.address_components;
      const components = {} as any;
      let company = "";
      if (
        addressComponent &&
        addressComponent.constructor === Array &&
        addressComponent.length
      ) {
        for (let i = 0; i < addressComponent.length; i++) {
          const component = addressComponent[i];
          const type = component.types[0];
          switch (type) {
            case "street_number":
              components.streetNumber = component.long_name;
              break;
            case "route":
              components.streetAddress =
                component.long_name +
                " " +
                (components.streetNumber &&
                !component.long_name.includes(components.streetNumber)
                  ? components.streetNumber
                  : "");
              components.address1 = component.short_name;
              break;
            case "postal_code":
              components.postalCode = component.long_name;
              break;
          }
        }
      }
      if (address && (address as any).company) {
        company = (address as any).company;
      } else {
        let name = address.name as string;
        if (name) {
          name = name.toLowerCase();
        } else {
          name = "";
        }
        if (
          typeof name !== "undefined" &&
          formattedAddress.includes(name) &&
          name !== components.postalCode
        ) {
          company = name;
        }
      }
      let streetName = components.address1;
      if (!streetName) {
        streetName = "";
      }
      if (
        streetName === company ||
        this.nameExists(
          company.toLowerCase(),
          addressComponent as google.maps.GeocoderAddressComponent[]
        )
      ) {
        company = "";
      }
      if (
        company &&
        !address.originalFormattedAddress
          ?.toLowerCase()
          ?.includes(company.toLowerCase())
      ) {
        const cloneDetails = _.cloneDeep(this.details);
        const details = cloneDetails?.[this.index]?.[direction]
          ? new Details(cloneDetails[this.index][direction])
          : new Details();
        details.company = company;
        this.setDetail({
          direction,
          value: details,
          index: this.index ? this.index : 0,
        });
      }
    },
    nameExists(
      name: string,
      addressComponents: google.maps.GeocoderAddressComponent[]
    ): boolean {
      if (name && name !== "" && addressComponents?.length) {
        return addressComponents.some(
          (component) =>
            component?.long_name?.toLowerCase().includes(name.toLowerCase()) ||
            component?.short_name?.toLowerCase().includes(name)
        );
      }
      return false;
    },
    checkIfInternational(
      address: google.maps.places.PlaceResult,
      direction: string
    ) {
      const addressComponent = address.address_components;
      let country = "";
      if (
        addressComponent &&
        addressComponent.constructor === Array &&
        addressComponent.length
      ) {
        for (let i = 0; i < addressComponent.length; i++) {
          const component = addressComponent[i];
          const type = component.types[0];
          switch (type) {
            case "country":
              country = component.short_name;
              break;
          }
        }
      }
      if (country && country !== "US") {
        if (direction === "to") {
          this.toInternational = true;
        } else {
          this.fromInternational = true;
        }
      } else if (direction === "to") {
        this.toInternational = false;
      } else {
        this.fromInternational = false;
      }
    },
    checkTrip(event) {
      const { id, direction } = event;
      // TODO: check if we can improve all proccess for that
      if (!this.trips?.[id]?.[direction]) {
        // this.setHasAdvancedInStep(true);
      }
    },
    async editDetailAddress(event) {
      const { id, direction, element, value, inputMethod, rawInput } = event;
      const detailsEdited = this.details.length
        ? _.cloneDeep(this.details)
        : [{ from: new Details(), to: new Details() }];
      // BIK-2189: `details` only grows when a stage is touched, so the index may be missing
      // (a hole, or past the end) — returning silently dropped a paste into stage 2.
      if (!detailsEdited[id]) {
        detailsEdited[id] = { from: new Details(), to: new Details() };
      }
      if (!detailsEdited[id][direction]) {
        return;
      }
      detailsEdited[id][direction][element] = value;
      if (element === "address1") {
        detailsEdited[id][direction].streetAddress = value;
        // BIK-2097: a hand-edit of the street field marks the entry as manual
        // and records the exact text the user typed.
        detailsEdited[id][direction].inputMethod = inputMethod ?? "manual";
        detailsEdited[id][direction].rawInput = rawInput ?? value;
      } else if (element === "streetAddress") {
        // BIK-2107: the dedicated Street Address field in the segmented fallback.
        // Keep address1/streetAddress in lockstep and force the segmented AVS route
        // (leave Region mode) so avsCheck sends address1/city/state/postal, not region.
        detailsEdited[id][direction].address1 = value;
        detailsEdited[id][direction].streetAddress = value;
        detailsEdited[id][direction].inputMethod = "manual";
        detailsEdited[id][direction].rawInput = null;
      } else if (
        (this as any).avsRawPasteDirect &&
        isRegionEntry(detailsEdited[id][direction])
      ) {
        // BIK-2107: the segmented fallback form is showing (an incomplete paste
        // couldn't be resolved in Region mode). Editing any segmented field
        // (city/state/country/postal/unit) must leave Region mode so avsCheck
        // sends the segmented params instead of re-running the stale one-liner.
        detailsEdited[id][direction].inputMethod = "manual";
        detailsEdited[id][direction].rawInput = null;
      }
      // BIK-2148: clearing the main input BY HAND must land the same state the X button
      // does. The X runs clearInput() → emitClearInput → clearInputAddress(), which resets
      // both the detail and the trip. Selecting all and deleting emits a plain address
      // edit instead, and StageForm.checkTrip() — the only other handler that fires — has
      // an empty body, so the trip kept the previous address. isSameLocation() compares
      // TRIPS, so an emptied Ship To still matched a Ship From set to the same saved
      // address and raised "Did you mean to ship from and to the same place?".
      //
      // Keyed on the resulting VALUE, not on the event: only when the street line is now
      // empty. A blur with text still in the field, or an edit that replaces the address,
      // falls through to the normal flow below, which rebuilds the trip itself.
      // Delegates to clearInputAddress so both paths cannot drift apart again.
      const isMainStreetField =
        element === "address1" || element === "streetAddress";
      const streetNowEmpty =
        !String(detailsEdited[id][direction].address1 ?? "").trim() &&
        !String(detailsEdited[id][direction].streetAddress ?? "").trim();
      if (isMainStreetField && streetNowEmpty) {
        this.clearInputAddress(direction);
        this.checkIfEqual();
        return;
      }
      if ((this as any).avsFeedbackEnabled) {
        detailsEdited[id][direction].avsChecked = false;
        detailsEdited[id][direction].passedAvs = false;
        detailsEdited[id][direction].avsProblem = false;
        if (detailsEdited[id][direction].avsStatus) {
          detailsEdited[id][direction].avsStatus.status = undefined;
          detailsEdited[id][direction].avsStatus.subStatus =
            AVSSubStatus.notCalled;
        }
      } else if (element !== "address2") {
        // BIK-1995 flag OFF: pre-rework reset — editing address2 did NOT
        // invalidate a previously passed AVS.
        detailsEdited[id][direction].avsChecked = false;
        detailsEdited[id][direction].passedAvs = false;
      }
      // BIK-2127: gate on the flag, matching avsCheck. isRegionEntry alone is true for a
      // legacy paste too (that path still sets inputMethod/rawInput for BIK-2097
      // telemetry), which skipped the trip build below and ran the paste-direct-only
      // Place/state-country resolution on the legacy flow.
      const isRegion =
        (this as any).avsRawPasteDirect &&
        isRegionEntry(detailsEdited[id][direction]);
      // For non-region entries keep the existing behavior: build the trip components from
      // the (already-segmented) detail before AVS runs. For a region paste the detail is
      // still a raw one-liner here (no city/state/zip yet), so we defer building the trip
      // until AFTER avsCheck fills the segmented fields (see below).
      if (!isRegion && this.trips[id]?.[direction]) {
        const trip = _.cloneDeep(this.trips[id][direction]);
        trip.address_components =
          detailsEdited[id][direction].mapComponentsToAddress();
        this.setTrip({ direction, value: trip, index: id });
      }
      const seq = ++this.avsCheckSeq[direction];
      this.onLoadingInput(true, direction);
      try {
        const { stageDetails, updatedDetails, changedFlags } = await avsCheck(
          detailsEdited[id][direction]
        );
        if (seq !== this.avsCheckSeq[direction]) {
          return;
        }
        if (stageDetails) {
          if (stageDetails.avsStatus?.status !== undefined) {
            stageDetails.avsChecked = true;
          }
          // BIK-2095: the region-paste path builds state/country from the AVS string
          // result ("TX"/"US") without the full option objects the UI and submit expect.
          // Resolve them against the allowed lists (same match setStage does for Places).
          if (isRegion) {
            (this as any).resolveStateCountryObjects(stageDetails);
            // The paste path never went through Google Places, so no trip Place was
            // created for this direction (Places builds it via @emitLocation). Rates reads
            // trip.from/.to (address_components, formatted_address, geometry), so build a
            // Place from the AVS-verified detail here. Missing geometry is fine — Rates
            // ignores coordinates (see StoreHelper.safeLatLng).
            const existing = this.trips[id]?.[direction];
            const placeData = existing ? _.cloneDeep(existing) : ({} as any);
            placeData.address_components = stageDetails.mapComponentsToAddress();
            placeData.formatted_address =
              Place.getFormattedAddress(stageDetails) ||
              stageDetails.streetAddress ||
              stageDetails.address1;
            const tripPlace = new Place(placeData);
            this.setTrip({ direction, value: tripPlace, index: id });
          }
          this.setDetail({ direction, value: stageDetails, index: id });
        }
        this.$emit("emitDetailsUpdated", {
          updatedDetails,
          direction,
          changedFlags,
        });
      } finally {
        if (seq === this.avsCheckSeq[direction]) {
          this.onLoadingInput(false, direction);
          // BIK-2148: the same-location check was wired to the two paths that existed
          // before the rework — assignLocation (Google Places) and assignUserDetails
          // (saved/event address). A pasted address builds its trip here instead, after
          // AVS resolves, so nothing re-ran the comparison and a paste that matched the
          // other side never raised the warning: the earlier passes had run while the
          // trip for this direction did not exist yet.
          //
          // Inside the seq guard so a superseded check (the user kept typing) does not
          // evaluate against a trip that is about to be replaced.
          this.checkIfEqual();
        }
      }
    },
    // BIK-2095: turn the region-paste string state/country into the full option objects
    // the state/country selects and the submit expect — mirrors Details.setStage's matching.
    // BIK-2140: body moved to the shared util, which every other address path now uses too.
    // Same matching, plus country name-matching and the mapState/null guards this copy lacked.
    resolveStateCountryObjects(detail: Details) {
      resolveStateCountryObjectsUtil(
        detail,
        (this as any).allowedCountries,
        (this as any).states
      );
    },
    clearInputAddress(direction: string) {
      const idx = this.index ? this.index : 0;
      this.setDetail({
        direction,
        value: "clear",
        index: idx,
      });
      this.setTrip({
        direction,
        value: new Trip(),
        index: idx,
      });
    },
    onLoadingInput(event: boolean, direction: string) {
      if (direction === "from") {
        this.isLoadingFrom = event;
      } else {
        this.isLoadingTo = event;
      }
      this.$emit("emitLoading", this.isLoadingFrom || this.isLoadingTo);
    },
    avsUiState(direction: string): string {
      // BIK-1995/BIK-2095: no inline AVS UI unless a feedback flag is on.
      if (!(this as any).avsFeedbackEnabled) return "idle";
      const detail = this.details?.[this.index]?.[direction] as Details | undefined;
      const isLoading = direction === "from" ? this.isLoadingFrom : this.isLoadingTo;
      if (isLoading) return "verifying";
      // BIK-2135: checked before avsChecked/passedAvs on purpose. UPS validates
      // that the address exists, not that we ship there, so a FedEx/USPS/PO Box
      // destination would otherwise render as a green "Verified" and only fail
      // at Search. Skipping the avsChecked guard also covers addresses that
      // never run avsCheck — saved addresses and event addresses arrive already
      // hydrated with avsChecked=true, so avsCheck's NeedsAVSCheck short-circuits.
      if (detail && getCarrierRestriction(detail)) {
        return "errorCarrierRestricted";
      }
      // BIK-2148: a UPS Store is deliverable, so this is a warning and not a block — but
      // it was only ever raised as an alert on Search, which lands while the page is in
      // its loading state and scrolls out of view. Surfaced inline instead, like every
      // other piece of Ship1 feedback. Checked BEFORE the avsChecked/passedAvs pair on
      // purpose: the address IS valid and would otherwise render a plain green
      // "Verified" with nothing said about the store.
      if (detail && getCarrierWarning(detail) === "upsStore") {
        return direction === "from" ? "upsStoreFrom" : "upsStoreTo";
      }
      if (!detail || !detail.avsChecked) return "idle";
      if (detail.passedAvs) return "verified";
      const sub = detail.avsStatus?.subStatus;
      // BIK-2148: checked before every error branch. A Google pick that resolved to a
      // city/ZIP was never sent to the validator — partial addresses are allowed so the
      // customer can quote — so it is neither verified nor failed, and must not render
      // as an error the user is expected to fix.
      if (sub === AVSSubStatus.zoneOnly) return "zoneOnly";
      if (sub === AVSSubStatus.unitNotFound) return "unitRequired";
      if (
        sub === AVSSubStatus.addressNotFound ||
        sub === AVSSubStatus.streetNotFound ||
        sub === AVSSubStatus.invalidAddress
      ) {
        return "errorAddress";
      }
      if (sub === AVSSubStatus.outOfRange) return "errorOutOfRange";
      if (sub === AVSSubStatus.noResultsReturned) return "errorNotActive";
      if (sub === AVSSubStatus.addressIncomplete) return "errorIncomplete";
      if (
        sub === AVSSubStatus.apiAVSFailed ||
        sub === AVSSubStatus.validLicenseKey ||
        sub === AVSSubStatus.rejectedByUser
      ) {
        return "apiFailed";
      }
      return "idle";
    },
    avsMessage(state: string): string {
      if (!(state in AVS_MESSAGE_FALLBACKS)) return "";
      return this.avsMessages[state]?.message ?? AVS_MESSAGE_FALLBACKS[state];
    },
    // imported functions (only for html use, if not needed use in html then remove)
    updateDetails,
    checkIfDetailUpdated,
  },
});
